import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import { useThemeMode } from './theme/ThemeModeContext';

// Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import ClientsPage from './pages/ClientsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import WorldCupCaseStudyPage from './pages/caseStudies/WorldCupCaseStudyPage';
import NotFoundPage from './pages/NotFoundPage';


function App() {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'he';
  // Light/dark mode (device setting or the header toggle). A mode change
  // re-renders here, re-reading the CSS tokens below, which switch on
  // <html data-theme> in colors-light.css.
  const { mode } = useThemeMode();
  
  // Get CSS variables for theming
  const getCssVar = (varName) => getComputedStyle(document.documentElement).getPropertyValue(varName).trim();

  // Create theme with direction and CSS variables
  const theme = createTheme({
    direction: isRtl ? 'rtl' : 'ltr',
    palette: {
      mode,
      primary: {
        main: getCssVar('--primary-color') || '#4A90E2',
        dark: getCssVar('--primary-dark') || '#007BFF',
        light: getCssVar('--primary-light') || '#7FB8F2',
      },
      secondary: {
        main: getCssVar('--secondary-color') || '#FFD700',
        dark: getCssVar('--secondary-dark') || '#E6C200',
        light: getCssVar('--secondary-light') || '#FFEB66',
      },
      text: {
        primary: getCssVar('--text-primary') || '#001F3F',
        secondary: getCssVar('--text-secondary') || '#007BFF',
      },
      background: {
        default: getCssVar('--bg-primary') || 'transparent',
        paper: getCssVar('--bg-secondary') || 'transparent',
      },
      error: {
        main: getCssVar('--danger-color') || '#dc3545',
      },
      warning: {
        main: getCssVar('--warning-color') || '#FFD700',
      },
      info: {
        main: getCssVar('--info-color') || '#4A90E2',
      },
      success: {
        main: getCssVar('--success-color') || '#28a745',
      },
    },
    typography: {
      fontFamily: isRtl ? 
        '"Assistant", "Roboto", "Helvetica", "Arial", sans-serif' : 
        '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: getCssVar('--header-bg') || '#001F3F',
            color: getCssVar('--header-text') || '#FFFFFF',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: getCssVar('--btn-primary-bg') || '#4A90E2',
            color: getCssVar('--btn-primary-text') || '#FFFFFF',
            '&:hover': {
              backgroundColor: getCssVar('--btn-primary-hover') || '#007BFF',
            },
          },
          containedSecondary: {
            backgroundColor: getCssVar('--btn-secondary-bg') || '#FFD700',
            color: getCssVar('--btn-secondary-text') || '#001F3F',
            '&:hover': {
              backgroundColor: getCssVar('--btn-secondary-hover') || '#E6C200',
            },
          },
          outlined: {
            borderColor: getCssVar('--btn-outline-border') || '#4A90E2',
            color: getCssVar('--btn-outline-text') || '#4A90E2',
            '&:hover': {
              backgroundColor: getCssVar('--btn-outline-hover-bg') || '#4A90E2',
              color: getCssVar('--btn-outline-hover-text') || '#FFFFFF',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: getCssVar('--card-bg') || 'transparent',
            borderColor: getCssVar('--card-border') || 'rgba(74, 144, 226, 0.2)',
            boxShadow: getCssVar('--shadow-md') || '0 4px 6px rgba(0, 31, 63, 0.1)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: getCssVar('--card-bg') || 'transparent',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: getCssVar('--input-border') || '#A8C8E8',
              },
              '&:hover fieldset': {
                borderColor: getCssVar('--input-focus') || '#4A90E2',
              },
              '&.Mui-focused fieldset': {
                borderColor: getCssVar('--input-focus') || '#4A90E2',
              },
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: getCssVar('--border-color') || '#A8C8E8',
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <ScrollToTop />
      {/* Header stays LTR always */}
      <div dir="ltr">
        <Header />
      </div>
      {/* Spacer to account for fixed header */}
      <Box sx={{ height: { xs: '56px', md: '64px' } }} />
      {/* Content respects RTL for Hebrew */}
      <div dir={isRtl ? 'rtl' : 'ltr'}>
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            {/* <Route path="/portfolio" element={<PortfolioPage />} /> */}
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/case-studies/world-cup-2026" element={<WorldCupCaseStudyPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default App;
